import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, ArrowRight, Cable, Zap, PlayCircle } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { translatePlanText, translatePlanMaxTech } from "@/lib/planTranslate";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Estrae un messaggio d'errore leggibile da qualsiasi risposta axios.
// FastAPI restituisce 422 con `detail` come array di oggetti Pydantic; se lo
// passiamo direttamente a toast/JSX React crasha (error #3). Qui normalizziamo
// sempre a stringa.
function extractErrorMsg(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => {
        if (typeof d === "string") return d;
        if (d && typeof d === "object") {
          const loc = Array.isArray(d.loc) ? d.loc.filter((x) => x !== "body").join(".") : "";
          return loc ? `${loc}: ${d.msg || "invalido"}` : (d.msg || "");
        }
        return "";
      })
      .filter(Boolean);
    if (parts.length) return parts.join(" · ");
  }
  if (detail && typeof detail === "object" && detail.msg) return String(detail.msg);
  if (err?.message && !err.response) return err.message;
  return fallback || "Errore imprevisto";
}

// Estrae ID YouTube dagli URL comuni. Ritorna null se non YouTube.
function toYouTubeEmbed(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace("www.", "");
    if (host === "youtu.be") return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    if (host === "youtube.com" || host === "m.youtube.com") {
      if (u.pathname.startsWith("/embed/")) return url;
      const v = u.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}`;
    }
    return null;
  } catch { return null; }
}
function toLoomEmbed(url) {
  if (!url || !url.includes("loom.com")) return null;
  return url.replace("/share/", "/embed/");
}
function toVimeoEmbed(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (!u.hostname.includes("vimeo.com")) return null;
    if (u.pathname.startsWith("/video/")) return url;
    const id = u.pathname.split("/").filter(Boolean).pop();
    if (id && /^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}`;
    return null;
  } catch { return null; }
}
function toEmbedUrl(url) {
  return toYouTubeEmbed(url) || toLoomEmbed(url) || toVimeoEmbed(url) || null;
}

export default function SignupLandingPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [step, setStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [form, setForm] = useState({ company_name: "", admin_name: "", admin_email: "", admin_password: "" });
  const [loading, setLoading] = useState(false);
  const [landing, setLanding] = useState({ video_url: "", video_title: "Guarda come funziona in 3 minuti", video_description: "" });

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/plans`).then((r) => setPlans(r.data)).catch(() => {});
    axios.get(`${BACKEND}/api/public/landing-config`).then((r) => setLanding(r.data)).catch(() => {});
  }, []);

  const pickPlan = (p) => { setSelectedPlan(p); setStep(2); };

  const signup = async () => {
    if (!form.company_name || !form.admin_email || !form.admin_password) return toast.error(t("auto.compila_tutti_i_campi_obbligatori"));
    if (form.admin_password.length < 6) return toast.error("Password minima 6 caratteri");
    setLoading(true);
    try {
      await axios.post(`${BACKEND}/api/auth/register`, {
        company_name: form.company_name,
        admin_name: form.admin_name || form.admin_email,
        admin_email: form.admin_email,
        admin_password: form.admin_password,
        // plan_id opzionale: signup senza carta per i primi 7 giorni
        plan_id: selectedPlan?.id || null,
      }, { withCredentials: true });
      toast.success("Azienda creata! Hai 7 giorni di prova gratuita, senza carta di credito.");
      // Redirect diretto in dashboard: la carta verrà chiesta alla scadenza del trial
      setTimeout(() => { window.location.href = "/dashboard"; }, 1200);
    } catch (e) {
      toast.error(extractErrorMsg(e, t("landing.signup_error")));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <Cable className="h-6 w-6 text-[#0052FF]" />
            <span className="text-lg font-bold text-slate-900">EVOSOFT</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher compact />
            <Link to="/login" className="text-sm text-slate-600 hover:text-[#0052FF]">
              {t("landing.already_account")} <b>{t("auth.login")}</b>
            </Link>
          </div>
        </div>
      </header>

      {step === 1 && (
        <div className="mx-auto max-w-6xl px-6 py-16">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1 text-xs font-bold uppercase tracking-wider text-emerald-800">
              <Zap className="h-3 w-3" /> {t("landing.trial_badge")}
            </div>
            <h1 className="mt-4 text-4xl font-bold text-slate-900 sm:text-5xl lg:text-6xl">
              {t("landing.main_title")}
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-600">
              {t("landing.main_subtitle")}
            </p>
          </div>

          {/* Video demo (se configurato dal super-admin) */}
          {landing.video_url && (() => {
            const embed = toEmbedUrl(landing.video_url);
            return (
              <div className="mx-auto mt-10 max-w-4xl" data-testid="landing-video">
                <div className="mb-3 flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500">
                  <PlayCircle className="h-4 w-4 text-red-600" /> {landing.video_title || t("landing.video_demo")}
                </div>
                <div className="relative overflow-hidden rounded-sm border border-slate-200 bg-black shadow-lg" style={{ paddingTop: "56.25%" }}>
                  {embed ? (
                    <iframe
                      src={embed}
                      title={landing.video_title || t("landing.video_demo")}
                      className="absolute inset-0 h-full w-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  ) : (
                    <a href={landing.video_url} target="_blank" rel="noopener noreferrer" className="absolute inset-0 grid place-items-center bg-slate-900 text-white hover:bg-slate-800">
                      <div className="text-center">
                        <PlayCircle className="mx-auto h-16 w-16" />
                        <div className="mt-2 text-sm">{t("landing.open_video")}</div>
                      </div>
                    </a>
                  )}
                </div>
                {landing.video_description && (
                  <p className="mt-3 text-center text-sm text-slate-500">{landing.video_description}</p>
                )}
              </div>
            );
          })()}

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {plans.map((p) => (
              <div key={p.id} className={`relative rounded-sm border-2 bg-white p-6 transition-all hover:border-[#0052FF] hover:shadow-lg ${p.code === "pro" ? "border-[#0052FF] shadow-md" : "border-slate-200"}`} data-testid={`landing-plan-${p.code}`}>
                {p.code === "pro" && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#0052FF] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">{t("landing.most_chosen")}</div>
                )}
                <div className="text-lg font-bold text-slate-900">{p.name}</div>
                {p.description && <div className="mt-1 text-sm text-slate-500">{translatePlanText(p.description, t)}</div>}
                <div className="mt-4 text-5xl font-bold text-[#0052FF]">€{Number(p.price_eur).toFixed(0)}<span className="text-base font-normal text-slate-500">{t("landing.per_month")}</span></div>
                {p.max_technicians && <div className="mt-1 text-xs text-slate-500">{translatePlanMaxTech(p.max_technicians, t)}</div>}
                {(p.features || []).length > 0 && (
                  <ul className="mt-5 space-y-2 text-sm text-slate-700">
                    {p.features.map((f, i) => <li key={i} className="flex gap-2"><CheckCircle2 className="h-4 w-4 flex-shrink-0 text-emerald-600" /> {translatePlanText(f, t)}</li>)}
                  </ul>
                )}
                <Button onClick={() => pickPlan(p)} className={`mt-6 w-full rounded-sm ${p.code === "pro" ? "bg-[#0052FF] hover:bg-[#0040CC]" : "bg-slate-900 hover:bg-slate-800"}`} data-testid={`landing-plan-pick-${p.code}`}>
                  {t("landing.activate_free_7")}
                </Button>
              </div>
            ))}
          </div>

          <div className="mt-16 rounded-sm border border-slate-200 bg-white p-8">
            <h2 className="text-2xl font-bold text-slate-900">{t("landing.features_title")}</h2>
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {[
                ["📦 " + t("landing.feature_warehouse"), t("landing.feature_warehouse_desc")],
                ["🚛 " + t("landing.feature_technicians"), t("landing.feature_technicians_desc")],
                ["📋 " + t("landing.feature_works"), t("landing.feature_works_desc")],
                ["🚗 " + t("landing.feature_vehicles"), t("landing.feature_vehicles_desc")],
              ].map(([title, desc], i) => (
                <div key={i} className="rounded-sm border border-slate-100 bg-slate-50 p-4">
                  <div className="text-sm font-bold text-slate-900">{title}</div>
                  <div className="mt-1 text-xs text-slate-600">{desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {step === 2 && selectedPlan && (
        <div className="mx-auto max-w-md px-6 py-16">
          <button onClick={() => setStep(1)} className="mb-4 text-sm text-slate-500 hover:text-slate-900">← {t("landing.change_plan")}</button>
          <div className="rounded-sm border-2 border-[#0052FF] bg-blue-50 p-4">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("landing.chosen_plan")}</div>
            <div className="mt-1 flex items-center justify-between">
              <div className="text-lg font-bold text-slate-900">{selectedPlan.name}</div>
              <div className="text-2xl font-bold text-[#0052FF]">€{Number(selectedPlan.price_eur).toFixed(0)}<span className="text-xs font-normal text-slate-500">{t("landing.per_month")}</span></div>
            </div>
            <div className="mt-1 text-xs text-emerald-700 font-semibold">{t("landing.trial_reminder")}</div>
          </div>

          <h1 className="mt-6 text-3xl font-bold text-slate-900">{t("landing.create_account_title")}</h1>
          <p className="mt-2 text-sm text-slate-500">{t("landing.create_account_subtitle")}</p>

          <div className="mt-6 space-y-3">
            <div>
              <Label>{t("landing.company_name")} *</Label>
              <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder={t("landing.company_name_ph")} className="rounded-sm" data-testid="signup-company" />
            </div>
            <div>
              <Label>{t("landing.your_name")}</Label>
              <Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} placeholder="Mario Rossi" className="rounded-sm" data-testid="signup-admin-name" />
            </div>
            <div>
              <Label>{t("landing.work_email")} *</Label>
              <Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} placeholder={t("landing.work_email_ph")} className="rounded-sm font-mono" data-testid="signup-admin-email" />
            </div>
            <div>
              <Label>{t("common.password")} *</Label>
              <Input type="password" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} placeholder={t("landing.password_ph")} className="rounded-sm font-mono" data-testid="signup-admin-password" />
            </div>
          </div>

          <Button onClick={signup} disabled={loading} className="mt-6 w-full rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="signup-submit">
            {loading ? t("landing.creating_account") : (<>{t("landing.activate_free_7")} <ArrowRight className="ml-2 h-4 w-4" /></>)}
          </Button>
          <p className="mt-3 text-center text-xs text-slate-500" dangerouslySetInnerHTML={{ __html: t("landing.signup_disclaimer") }} />
        </div>
      )}
    </div>
  );
}
